import { useEffect, useRef, useState } from "react";
import { useT } from "@/i18n";

// TRE guided tremor-release timer. Value: { current_phase, phases_done[],
// tremor_intensity, completed }. Validates at >= 1 completed phase.
const PHASES = [
  { key: "warmup", labelKey: "tre_phase_warmup", seconds: 60 },
  { key: "fatigue", labelKey: "tre_phase_fatigue", seconds: 180 },
  { key: "tremor", labelKey: "tre_phase_tremor", seconds: 180 },
  { key: "rest", labelKey: "tre_phase_rest", seconds: 60 },
];

export function TREGuide({ value, onChange }) {
  const t = useT();
  const [phaseIdx, setPhaseIdx] = useState(value?.current_phase || 0);
  const [running, setRunning] = useState(false);
  const [remaining, setRemaining] = useState(PHASES[value?.current_phase || 0].seconds);
  const [intensity, setIntensity] = useState(value?.tremor_intensity || 0);
  const tid = useRef(null);

  useEffect(() => {
    if (!running) { if (tid.current) clearInterval(tid.current); return; }
    tid.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(tid.current);
          setRunning(false);
          if (phaseIdx < PHASES.length - 1) {
            const next = phaseIdx + 1;
            setPhaseIdx(next);
            setTimeout(() => setRemaining(PHASES[next].seconds), 0);
            onChange({ ...(value || {}), current_phase: next, phases_done: [...(value?.phases_done || []), PHASES[phaseIdx].key], tremor_intensity: intensity });
          } else {
            onChange({ ...(value || {}), current_phase: PHASES.length, completed: true, phases_done: PHASES.map((p) => p.key), tremor_intensity: intensity });
          }
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => { if (tid.current) clearInterval(tid.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, phaseIdx]);

  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  const currentPhase = PHASES[Math.min(phaseIdx, PHASES.length - 1)];
  const btnLabel = running ? t("widget_pause") : phaseIdx === 0 && remaining === PHASES[0].seconds ? t("widget_start") : t("widget_resume");

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">{t("tre_hint")}</p>
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-border bg-secondary/50 p-6">
        <p className="font-display text-sm text-foreground">{t(currentPhase.labelKey)}</p>
        <p className="font-display text-5xl tracking-widest text-primary" data-testid="tre-timer">{mm}:{ss}</p>
        <button
          type="button"
          data-testid="tre-toggle"
          onClick={() => setRunning((r) => !r)}
          className="rounded-full px-6 py-2.5 text-sm font-semibold text-white"
          style={{ backgroundColor: running ? "#C7893B" : "var(--primary, #5E7A73)" }}
        >
          {btnLabel}
        </button>
      </div>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("tre_intensity_lbl")}</p>
      <div className="flex flex-wrap gap-1.5">
        {Array.from({ length: 11 }, (_, i) => i).map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => { setIntensity(n); onChange({ ...(value || {}), tremor_intensity: n }); }}
            className={`h-9 w-9 rounded-full text-sm font-display transition-colors ${intensity === n ? "bg-primary text-primary-foreground" : "border border-border bg-secondary/60 text-foreground"}`}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}
